import { Authenticator } from "@aws-amplify/ui-react";
import { ColorScheme, ColorSchemeProvider, MantineProvider } from "@mantine/core";
import { NotificationsProvider } from "@mantine/notifications";
import { configureRestClients, RpiApiProvider } from "@rpi/openapi-api";
import { rpiTheme } from "@rpi/openapi-core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Amplify, Auth } from "aws-amplify";
import React from "react";
import { RouterProvider } from "react-router-dom";
import { $envRibbon } from ".";
import { RpiModalsProvider } from "./components/modals/modals";
import { RumErrorBoundary } from "./components/RumErrorBoundary";
import { useDevMode } from "./hooks/useDevMode";
import { router } from "./router";
import { initializeRum } from "./rum/cloudwatchRum";
import { Error404View } from "./views/error/Error404View";
import axios from "axios";

// Initialize CloudWatch RUM as early as possible, before any other SDK
initializeRum();

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: import.meta.env.VITE_COGNITO_REGION,
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_WEB_CLIENT_ID,
  },
  oauth: {
    domain: import.meta.env.VITE_COGNITO_CUSTOM_DOMAIN,
    scope: ["phone", "email", "profile", "openid"],
    redirectSignIn: `${window.location.origin}/`,
    redirectSignOut: `${window.location.origin}/`,
    responseType: "code",
  },
  Analytics: {
    disabled: true,
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const graphqlClient = axios.create({
  baseURL: import.meta.env.VITE_GRAPHQL_CLIENT_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

configureRestClients(import.meta.env.VITE_PUBLIC_PRINTAPI_ENDPOINT_URL, import.meta.env.VITE_CLIENT_API_URL);

const notificationPosition = "top-center";
const notificationMaxWidth = 1020;

export default function App() {
  const { toggleDevMode } = useDevMode();
  const [colorScheme, setColorScheme] = React.useState<ColorScheme>("light");

  const toggleColorScheme = (value?: ColorScheme) => {
    const nextColorScheme = value || (colorScheme === "dark" ? "light" : "dark");
    setColorScheme(nextColorScheme);
  };

  React.useEffect(() => {
    const handleClick = () => toggleDevMode();

    $envRibbon.addEventListener("click", handleClick);
    return () => {
      $envRibbon.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <RumErrorBoundary>
      <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
        <MantineProvider theme={{ colorScheme, ...rpiTheme }} withGlobalStyles>
          <NotificationsProvider position={notificationPosition} containerWidth={notificationMaxWidth}>
            <Authenticator.Provider>
              <QueryClientProvider client={queryClient}>
                <RpiApiProvider
                  value={{
                    graphqlClient,
                    headers: async () => {
                      // TODO: Cache?
                      const session = await Auth.currentSession();
                      const jwtToken = session.getAccessToken().getJwtToken();

                      return {
                        Authorization: jwtToken,
                      };
                    },
                  }}
                >
                  <RpiModalsProvider>
                    <RouterProvider router={router} fallbackElement={<Error404View />} />
                  </RpiModalsProvider>
                </RpiApiProvider>
              </QueryClientProvider>
            </Authenticator.Provider>
          </NotificationsProvider>
        </MantineProvider>
      </ColorSchemeProvider>
    </RumErrorBoundary>
  );
}
